import React from 'react'
import { Dialog, IconButton, Divider, CircularProgress, Button, Box, Link } from '@material-ui/core'
import { ClassNames } from '@emotion/react'
import { makeStyles } from '@material-ui/core/styles'
import i18n from 'common/util/internationalization'
import { AIGenerationIcon } from 'assets/icons/AiGenerationIcon'
import { AtlasIcon } from 'web-components/atlas-icon'
import ErrorIcon from 'assets/icons/ErrorIcon'
import UploadIcon from 'assets/icons/UploadIcon'
import {
    ColorEastBay,
    AtlasGreyLight,
    ColorBaseMulberryBlue,
    ColorBaseWhite
} from 'assets/styles/variables'
import {
    UploadContainer,
    UploadInput,
    UploadText,
    UploadLink,
    UploadInfo,
    UploadSection,
    UploadIconStyle,
    UploadFileDetails,
    HighlightedText,
    StyledDeleteSectionIcon,
    StyledSuccessIcon,
    UploadFileSize,
    UploadFileError
} from './UploadStyles'
import {
    HeaderText,
    ProgressMessage,
    aiDialogContainer,
    aiDialogHeaderWithGradient,
    aiGenerateFooter,
    customButton
} from '../sectionlist/PregeneratedMinutesStyles'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import { TRANSCRIPT_UPLOAD_STATUS } from '../components/InvitationView/types'
import { getLocalStorageItem } from 'businesslayer/minutesLocalStore'
import { getLearnMoreURL } from 'common/util/urlMap'

const useStyles = makeStyles({
    myIconButton: {
        marginLeft: 'auto',
        '&:focus': {
            border: `2px solid ${ColorBaseMulberryBlue}`,
            padding: '5px',
            borderRadius: '40px'
        }
    },
    myButton: {
        '&:focus, &:active': {
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px'
        }
    },
    inaccuraciesContainer: {
        display: 'flex',
        padding: '4px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px'
    },
    inaccuraciesText: {
        color: '#1E1E1E',
        fontFamily: 'Source Sans Pro',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px',
        letterSpacing: 0
    },
    learnMoreLink: {
        color: '#385F99',
        fontFamily: 'Source Sans Pro',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '18px',
        letterSpacing: 0,
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'none'
        }
    }
})

const getUploadErrorMessage = (status, error, finalDuration) => {
    if (error) return error
    const messages = {
        [TRANSCRIPT_UPLOAD_STATUS.FILE_NAME_LENGTH]: 'AI_TTM_FILE_NAME_LENGTH_ERROR',
        [TRANSCRIPT_UPLOAD_STATUS.EMPTY_FILE]: 'AI_TTM_EMPTY_FILE',
        [TRANSCRIPT_UPLOAD_STATUS.FILE_SIZE_LIMIT]: 'AI_TTM_MAX_FILE_SIZE_ERROR',
        [TRANSCRIPT_UPLOAD_STATUS.WORD_COUNT_LIMIT]: 'AI_TTM_MAX_DURATION_ERROR'
    }
    return i18n.t(
        messages[status] || 'NETWORK_ERROR',
        status === TRANSCRIPT_UPLOAD_STATUS.WORD_COUNT_LIMIT ? { duration: finalDuration } : {}
    )
}

const isErroredStatus = (status) =>
    [
        TRANSCRIPT_UPLOAD_STATUS.FAILED,
        TRANSCRIPT_UPLOAD_STATUS.UNSUPPORTED_FORMAT,
        TRANSCRIPT_UPLOAD_STATUS.FILE_NAME_LENGTH,
        TRANSCRIPT_UPLOAD_STATUS.EMPTY_FILE,
        TRANSCRIPT_UPLOAD_STATUS.FILE_SIZE_LIMIT,
        TRANSCRIPT_UPLOAD_STATUS.WORD_COUNT_LIMIT
    ].includes(status)

interface FileItem {
    name: string
    size: number
    file: File | null
}

interface TtmUploadDialogProps {
    updateFileDetailsAndStatus: (file: FileItem, status: string) => void
    handleOpenUploadModal: (...args: any[]) => void
    handleSelectTranscriptFile: (...args: any[]) => void
    handleOpenTtmModal: (...args: any[]) => void
    uploadModal: boolean
    uploadStatus: string
    items: FileItem
    deleteFileForTranscript: (...args: any[]) => void
    transcriptId: string
    fileUploadError: string
}

const TtmUploadDialog = ({
    uploadModal,
    handleOpenUploadModal,
    handleSelectTranscriptFile,
    handleOpenTtmModal,
    updateFileDetailsAndStatus,
    uploadStatus,
    items,
    deleteFileForTranscript,
    transcriptId,
    fileUploadError
}: TtmUploadDialogProps) => {
    const classes = useStyles()
    const userLanguage = getLocalStorageItem('userLanguage')

    const resetUpload = (closeModal) => {
        if (closeModal) handleOpenUploadModal(uploadModal)
        if (transcriptId) deleteFileForTranscript(transcriptId)
        updateFileDetailsAndStatus(
            { name: '', size: 0, file: null },
            TRANSCRIPT_UPLOAD_STATUS.DEFAULT
        )
    }

    const handleDrop = (e) => {
        e.preventDefault()
        const file = e.dataTransfer.files?.[0]
        if (!file) return

        const validTypes = ['.vtt', '.txt', '.docx']
        const ext = `.${file.name.split('.').pop()}`

        if (validTypes.includes(ext)) {
            handleSelectTranscriptFile({ target: { files: [file] } })
        } else {
            updateFileDetailsAndStatus(file, TRANSCRIPT_UPLOAD_STATUS.UNSUPPORTED_FORMAT)
        }
    }

    const renderFileSize = (size: number) =>
        size < 1048576 ? `${(size / 1024).toFixed(2)} KB` : `${(size / 1048576).toFixed(2)} MB`

    const wordCount = window._env_.TTM_MAX_WORDCOUNT || 130000
    const duration = Math.ceil((wordCount / 240 / 60) * 10) / 10 // Calculating duration in hours and rounding to one decimal place
    const finalDuration = duration > 2 ? Math.floor(duration) : duration

    const renderUploadStatus = (finalDuration) => {
        if (uploadStatus === TRANSCRIPT_UPLOAD_STATUS.DEFAULT) {
            return (
                <>
                    <UploadContainer
                        data-analytics="MM-AI-Transcript-Upload"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}>
                        <UploadInput
                            type="file"
                            accept=".vtt,.txt,.docx"
                            onChange={handleSelectTranscriptFile}
                            id="fileUpload"
                        />
                        <UploadText>
                            <UploadIconStyle>
                                <UploadIcon />
                            </UploadIconStyle>
                            <label
                                htmlFor="fileUpload"
                                style={{ cursor: 'pointer', display: 'inline-block' }}
                                tabIndex={0}
                                onKeyDown={(e) =>
                                    ['Enter', ' '].includes(e.key) &&
                                    document.getElementById('fileUpload')?.click()
                                }>
                                <UploadLink as="span">{i18n.t('AI_TTM_UPLOAD_DOC')}</UploadLink>
                            </label>
                            | <span>{i18n.t('AI_TTM_DRAG_DROP')}</span>
                        </UploadText>
                    </UploadContainer>
                    <UploadInfo>
                        {i18n.t('AI_TTM_FORMAT_SUPPORTED')}
                        <br />
                        {i18n.t('AI_TTM_MAX_FILE_SIZE')}
                        <br />
                        {i18n.t('AI_TTM_MAX_DURATION', { duration: finalDuration })}
                    </UploadInfo>
                </>
            )
        }

        return (
            <UploadSection>
                {uploadStatus === TRANSCRIPT_UPLOAD_STATUS.PROGRESS && (
                    <CircularProgress size={34} thickness={4} color="primary" />
                )}
                {uploadStatus === TRANSCRIPT_UPLOAD_STATUS.SUCCESS && <StyledSuccessIcon />}
                {isErroredStatus(uploadStatus) && <ErrorIcon />}

                <UploadFileDetails>
                    <HighlightedText>{items?.name}</HighlightedText>
                    {uploadStatus === TRANSCRIPT_UPLOAD_STATUS.SUCCESS && (
                        <UploadFileSize>{renderFileSize(items?.size)}</UploadFileSize>
                    )}
                    {isErroredStatus(uploadStatus) && (
                        <UploadFileError>
                            {uploadStatus === TRANSCRIPT_UPLOAD_STATUS.UNSUPPORTED_FORMAT
                                ? i18n.t('AI_TTM_UNSUPPORTED_FILE')
                                : getUploadErrorMessage(
                                      uploadStatus,
                                      fileUploadError,
                                      finalDuration
                                  )}
                        </UploadFileError>
                    )}
                </UploadFileDetails>

                {(uploadStatus === TRANSCRIPT_UPLOAD_STATUS.SUCCESS ||
                    isErroredStatus(uploadStatus)) && (
                    <AtlasTooltip
                        title={i18n.t('DELETE_FILE') || 'Delete File'}
                        placement="top-start">
                        <IconButton
                            tabIndex={0}
                            disableFocusRipple
                            disableRipple
                            className={classes.myIconButton}
                            data-analytics="MM-AI-Transcript-Delete"
                            onClick={() => resetUpload(false)}>
                            <StyledDeleteSectionIcon />
                        </IconButton>
                    </AtlasTooltip>
                )}
            </UploadSection>
        )
    }

    return (
        <ClassNames>
            {({ css }) => (
                <Dialog
                    open={uploadModal}
                    onClose={() => handleOpenUploadModal(uploadModal)}
                    disableBackdropClick
                    disableEscapeKeyDown
                    className={css`
                        .MuiPaper-root.MuiDialog-paper {
                            max-width: 1000px;
                            width: 1000px;
                            max-height: 100%;
                            border-radius: 8px;
                        }
                    `}>
                    <div className={aiDialogContainer}>
                        <div className={aiDialogHeaderWithGradient}>
                            <AIGenerationIcon />
                            <HeaderText>{i18n.t('AI_TTM_MINUTES')}</HeaderText>
                            <IconButton
                                tabIndex={0}
                                disableFocusRipple
                                disableRipple
                                className={classes.myIconButton}
                                onClick={() => resetUpload(true)}>
                                <AtlasIcon
                                    name="close"
                                    size={24}
                                    data-testid="AtlasModal_CloseIcon"
                                />
                            </IconButton>
                        </div>
                        <ProgressMessage>{i18n.t('AI_TTM_DESCRIPTION')}</ProgressMessage>
                        <ProgressMessage>{renderUploadStatus(finalDuration)}</ProgressMessage>
                        <Divider />
                        <Box className={aiGenerateFooter}>
                            <Box className={classes.inaccuraciesContainer}>
                                <span className={classes.inaccuraciesText}>
                                    {i18n.t('AI_INACCURACIES')}
                                </span>

                                <Link
                                    className={classes.learnMoreLink}
                                    style={{ textDecoration: 'none' }}
                                    href={getLearnMoreURL(userLanguage)}
                                    target="blank">
                                    {i18n.t('MINUTES_TEMPLATES_LEARN_MORE')}
                                </Link>
                            </Box>
                            <Button
                                className={`${classes.myButton} ${customButton}`}
                                disableRipple
                                disableFocusRipple
                                style={{
                                    backgroundColor:
                                        uploadStatus === TRANSCRIPT_UPLOAD_STATUS.SUCCESS
                                            ? ColorEastBay
                                            : AtlasGreyLight,
                                    color:
                                        uploadStatus === TRANSCRIPT_UPLOAD_STATUS.SUCCESS
                                            ? ColorBaseWhite
                                            : '#B3B3B3',
                                    opacity: 1,
                                    pointerEvents:
                                        uploadStatus === TRANSCRIPT_UPLOAD_STATUS.SUCCESS
                                            ? 'auto'
                                            : 'none'
                                }}
                                onClick={handleOpenTtmModal}
                                data-analytics="MM-AI-Transcript-Generate"
                                disabled={uploadStatus !== TRANSCRIPT_UPLOAD_STATUS.SUCCESS}>
                                {i18n.t('AI_TTM_START_GENERATING')}
                            </Button>
                        </Box>
                    </div>
                </Dialog>
            )}
        </ClassNames>
    )
}

export default TtmUploadDialog
